import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Tag, ShieldAlert } from 'lucide-react';
import { AppShell } from '@/components/app-shell';
import { api, type CoverageGapsResponse } from '@/lib/api';

export function CoverageGapsPage() {
  const { getToken } = useAuth();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['coverage-gaps'],
    queryFn: () =>
      api<CoverageGapsResponse>('/api/vendors/coverage-gaps', {
        tokenGetter: () => getToken(),
      }),
    staleTime: 60_000,
  });

  return (
    <AppShell
      title="Coverage gaps"
      subtitle="Aircraft platforms and part categories where vendor coverage is thin"
    >
      <div className="h-full overflow-auto px-6 py-6 space-y-4">
        {isLoading && (
          <div className="card p-8 text-sm text-foreground-muted text-center">
            Analyzing roster…
          </div>
        )}
        {isError && (
          <div className="card p-8 text-sm text-danger text-center">
            {(error as { message?: string })?.message ?? 'Failed to load coverage gaps'}
          </div>
        )}

        {data && (
          <>
            {/* Thin coverage section */}
            <section className="card overflow-hidden">
              <header className="px-5 py-3 border-b border-border flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ShieldAlert className="w-4 h-4 text-warning" />
                  <h2 className="font-semibold text-sm">Platforms with thin coverage</h2>
                </div>
                <span className="pill pill-warning">
                  {data.platformsWithThinCoverage.length}
                </span>
              </header>
              <p className="px-5 py-2.5 text-xs text-foreground-muted bg-surface-muted/40 border-b border-border">
                Aircraft platforms with <span className="font-semibold text-foreground">&lt; 2 distinct vendors</span> who quoted in the last 12 months. Add or recruit vendors before the next AOG hits.
              </p>
              {data.platformsWithThinCoverage.length === 0 ? (
                <div className="px-5 py-8 text-sm text-foreground-muted text-center italic">
                  Every active platform has ≥2 vendors. ✨
                </div>
              ) : (
                <ul className="divide-y divide-border">
                  {data.platformsWithThinCoverage.map((p) => (
                    <li key={p.platform} className="px-5 py-3 flex items-baseline justify-between gap-3 flex-wrap">
                      <div className="flex items-baseline gap-2.5">
                        <span className="font-mono font-semibold text-sm">{p.platform}</span>
                        <span className="pill pill-default">
                          {p.vendorCount} vendor{p.vendorCount === 1 ? '' : 's'}
                        </span>
                      </div>
                      <div className="text-xs text-foreground-muted">
                        {p.rfqCount} RFQ{p.rfqCount === 1 ? '' : 's'} affected
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* Untagged platforms section */}
            <section className="card overflow-hidden">
              <header className="px-5 py-3 border-b border-border flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Tag className="w-4 h-4 text-primary-strong" />
                  <h2 className="font-semibold text-sm">Untagged frequent platforms</h2>
                </div>
                <span className="pill pill-info">
                  {data.untaggedPlatforms.length}
                </span>
              </header>
              <p className="px-5 py-2.5 text-xs text-foreground-muted bg-surface-muted/40 border-b border-border">
                Aircraft platforms appearing in <span className="font-semibold text-foreground">≥3 RFQs</span> with no vendor specialization tagged. Tag at least one vendor so coverage tracking works.
              </p>
              {data.untaggedPlatforms.length === 0 ? (
                <div className="px-5 py-8 text-sm text-foreground-muted text-center italic">
                  Every frequent platform is tagged. Coverage tracking is healthy.
                </div>
              ) : (
                <ul className="divide-y divide-border">
                  {data.untaggedPlatforms.map((p) => (
                    <li key={p.platform} className="px-5 py-3 flex items-baseline justify-between gap-3 flex-wrap">
                      <span className="font-mono font-semibold text-sm">{p.platform}</span>
                      <div className="text-xs text-foreground-muted">
                        {p.rfqCount} RFQ{p.rfqCount === 1 ? '' : 's'} · no vendor tagged
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {data.platformsWithThinCoverage.length === 0 &&
              data.untaggedPlatforms.length === 0 && (
                <div className="card p-12 text-center">
                  <div className="w-12 h-12 mx-auto rounded-full bg-success-muted text-success flex items-center justify-center mb-4">
                    <AlertTriangle className="w-6 h-6" />
                  </div>
                  <h3 className="font-semibold mb-1">No coverage gaps</h3>
                  <p className="text-sm text-foreground-muted">
                    Your roster is healthy — every active platform has multiple vendors and tagged coverage.
                  </p>
                </div>
              )}
          </>
        )}
      </div>
    </AppShell>
  );
}
